import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/languageStore';
import { useWebsite } from '../contexts/websiteStore';

export function Hero() {
    const { t, isRTL } = useLanguage();
    const { config, getAsset } = useWebsite();
    const containerRef = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end start"]
    });

    const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

    const scrollToServices = () => {
        const servicesSection = document.getElementById('services');
        servicesSection?.scrollIntoView({ behavior: 'smooth' });
    };

    const downloadProfile = () => {
        const link = document.createElement('a');
        link.href = getAsset(config.assets.profilePdf || 'profile.pdf');
        link.download = `${config.name} Profile.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <section ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden mb-20">
            {/* Interactive gradient background */}
            <motion.div
                className="absolute inset-0"
                transition={{
                    duration: 0.5,
                    ease: "easeOut"
                }}
            />

            {/* Background Image */}
            <motion.div
                className="absolute inset-0"
                style={{ y, opacity }}
            >
                <img
                    src={getAsset(config.assets.heroBackground || 'hero.jpg')}
                    alt="Professional Background"
                    className="w-full h-full object-cover"
                />
            </motion.div>

            {/* Unified Overlay System */}
            <div className="absolute inset-0">
                {/* Primary overlay - dark gradient for text readability */}
                <motion.div
                    className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.5 }}
                />

                {/* Left top dark overlay */}
                <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-black/65 via-black/25 to-transparent"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.5, delay: 0.2 }}
                />

                {/* Text contrast overlay - stronger dark for text area */}
                <motion.div
                    className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.5, delay: 0.1 }}
                />

                {/* Secondary overlay - brand color accents */}
                <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-primary/12 via-transparent to-secondary/12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.5, delay: 0.3 }}
                />

                {/* Tertiary overlay - subtle corner gradients */}
                <motion.div
                    className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-primary/6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.5, delay: 0.6 }}
                />

                {/* Floating accent element */}
                <motion.div
                    className={`absolute top-1/5 ${isRTL ? 'left-1/5' : 'right-1/5'} w-3 h-3 bg-primary/80 rounded-full shadow-lg shadow-primary/50`}
                    animate={{
                        y: [0, -10, 0],
                        opacity: [0.6, 1, 0.6],
                        scale: [1, 1.2, 1]
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: 0.5
                    }}
                />
            </div>

            <div className={`relative z-10 container mx-auto px-6 ${isRTL ? 'text-right' : 'text-left'} mt-24`}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-6xl mx-auto"
                >
                    {/* Luxury Main Title */}
                    <motion.h1
                        className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-[0.9] tracking-wide font-secondary"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 1.2, ease: "easeOut" }}
                    >
                        <motion.span
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 1.2, ease: "easeOut" }}
                            className="block"
                        >
                            {t("hero.site_name_first")}
                        </motion.span>
                        <motion.span
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 1.2, ease: "easeOut" }}
                            style={{
                                color: 'var(--primary-color)',
                                fontWeight: 'bold',
                                filter: config.name.toLowerCase() === 'emcor' || config.name.toLowerCase() === 'citex' ? 'brightness(1.5)' : 'brightness(3.5)'
                            }}
                            className="block"
                        >
                            {t("hero.site_name_second")}
                        </motion.span>
                    </motion.h1>

                    {/* Headline */}
                    <motion.p
                        className="text-base md:text-lg text-white/90 mb-8 leading-tight tracking-wide font-primary"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.0, duration: 1.2, ease: "easeOut" }}
                        style={{
                            maxWidth: '500px'
                        }}
                    >
                        {t("hero.slogan1")}
                    </motion.p>

                    {/* Trusted By */}
                    <motion.p
                        className="text-sm md:text-base text-white/70 mb-12 max-w-2xl leading-relaxed tracking-wide"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 1.2, ease: "easeOut" }}
                    >
                        {t("hero.trustedBy")}
                    </motion.p>

                    {/* CTA Buttons */}
                    <motion.div
                        className={`flex flex-col sm:flex-row gap-8 items-start mb-20`}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 1.2, ease: "easeOut" }}
                    >
                        <motion.div
                            whileHover={{ y: -2 }}
                            transition={{ duration: 0.3 }}
                        >
                            <button
                                onClick={scrollToServices}
                                className="bg-white text-black hover:bg-white/90 px-12 py-4 text-sm font-light tracking-wider border-0 transition-all duration-300 min-w-[200px] group rounded"
                            >
                                <span className="relative z-10 flex items-center">
                                    {t("hero.exploreServices")}
                                    <ArrowRight className={`${isRTL ? 'mr-3 rotate-180' : 'ml-3'} h-4 w-4 transition-transform group-hover:translate-x-1`} />
                                </span>
                            </button>
                        </motion.div>

                        <motion.div
                            whileHover={{ y: -2 }}
                            transition={{ duration: 0.3 }}
                        >
                            <button
                                onClick={downloadProfile}
                                className="border-2 border-white/30 text-white hover:bg-white/10 hover:text-white px-12 py-4 text-sm font-light tracking-wider transition-all duration-300 min-w-[200px] backdrop-blur-sm bg-black/20 rounded"
                            >
                                {t("hero.viewPortfolio")}
                            </button>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
